/**
 * Hook consolidado para sincronização realtime de configurações
 * Consolida: useIntelligenceSync (4 channels → 1)
 * Otimização v2.1: Só ativa quando intelligence_mode != OFF
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { realtime } from '@/lib/realtime';
import { useIntelligenceMode } from '@/hooks/useIntelligenceMode';
import { invalidateConfigQueries } from '@/utils/queryInvalidation';
import { stockCapabilityKeys } from '@/hooks/useStockCapabilities';

export const useConfigSync = () => {
  const queryClient = useQueryClient();
  const { mode } = useIntelligenceMode();

  useEffect(() => {
    // Walter 2026-05-17: removido guard `isSystemActive()` (ver useProductionCoreSync).
    // Walter 2026-05-18 — revisão realtime #0: removido early return em
    // `mode === 'OFF'`. Antes, com intelligence OFF, TODOS os listeners de config
    // ficavam desativados — mudanças em user_roles, tenant_integrations,
    // environment_config, routing_rules etc não chegavam ao frontend. Listeners
    // de config são lightweight (poucas mudanças/dia); manter sempre ativos.

    console.log('🔔 Config Sync: Inicializando channel consolidado (6 listeners, modo:', mode, ')');

    const channel = realtime
      .channel('config-sync')
      
      // Listener 1: Intelligence Mode
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'environment_config', 
          filter: 'config_key=eq.intelligence_mode' },
        (payload) => {
          console.log('🔔 Config: intelligence_mode mudou', payload);
          queryClient.invalidateQueries({ queryKey: ['intelligence-mode'] });
        }
      )
      
      // Listener 2: Order Metadata
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'order_metadata' },
        (payload) => {
          console.log('🔔 Config: order_metadata mudou', payload.eventType);
          
          const orderId = payload.new && 'order_id' in payload.new 
            ? (payload.new as { order_id?: string }).order_id 
            : undefined;
          
          invalidateConfigQueries(queryClient, { 
            metadataOrderId: orderId 
          });
        }
      )
      
      // (2026-10-08, auditoria A14) Saíram os ouvintes de extraction_config e de
      // environment_config.trello_sync_health: invalidavam chaves que nenhuma
      // consulta usa — nada escutava.

      // Listener 6: User Roles (chat-contacts + permissões)
      // Walter 2026-05-18 — revisão realtime #0: queryKey ['chat-contacts'] estava
      // sem invalidação automática. Quando admin cria novo usuário, contato não
      // aparecia na lista até refresh. Também invalida user-related queries.
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'user_roles' },
        (payload) => {
          console.log('🔔 Config: user_roles mudou', payload.eventType);
          queryClient.invalidateQueries({ queryKey: ['chat-contacts'] });
        }
      )
      // Listener 7: Modo de implantação do estoque (0082)
      //
      // É o que faz "desligar" valer em segundos, no chão de fábrica, sem
      // depender de deploy (§4.1: redeploy exige humano e o host já recusou os
      // runners). O gerente desliga a bipagem no painel e a tela do operador
      // volta ao caminho antigo sem ninguém dar F5.
      //
      // SEM filtro por chave, ao contrário dos listeners 1 e 5: são 7 chaves, e
      // 7 listeners filtrados custariam 7 entradas para fazer o que um prefixo
      // resolve. Config muda pouquíssimas vezes por dia — o custo de acordar
      // neste callback e sair fora é irrelevante.
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'environment_config' },
        (payload) => {
          const chave = (payload.new as { config_key?: string })?.config_key;
          if (!chave?.startsWith('estoque_modo_')) return;
          console.log('🔔 Config: capacidade de estoque mudou', chave);
          queryClient.invalidateQueries({ queryKey: stockCapabilityKeys.all });
        }
      )
      // Listener 8: capacidade da fábrica (a gestão salva em outra aba → a tela recarrega)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'scheduler_params' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['capacidade-fabrica'] });
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('✅ Config Sync: 8 listeners ativos em 1 channel');
        }
      });

    return () => {
      console.log('🔕 Config Sync: Removendo channel');
      realtime.removeChannel(channel);
    };
  }, [queryClient, mode]);
};
